import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Outlet, useLocation } from 'react-router'

import { type Invite, InviteStatus, type Role } from '@ankor-io/common/types/account'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { TabItem, Tabs } from '@/ui-components/Tabs'
import InviteUserModal from '@/views/components/InviteUserModal'

const ConnectedAccountUsers = () => {
  const { getAccessToken } = useKindeAuth()
  const location = useLocation()
  const { connectedAccounts } = useConnectedAccount()
  const { activeConnectedAccountId, activeOrganizationId } = useUserContext()

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [newlyInvitedUser, setNewlyInvitedUser] = useState<Invite | null>(null)

  const handleUserInvite = async (userData: { email: string; role: Role; connectedAccountId?: string }) => {
    const handleInvite = async (userData: { email: string; role: Role; connectedAccountId?: string }) => {
      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/users/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({
            email: userData.email,
            roles: [userData.role],
            awxConnectedAccountId: userData.connectedAccountId,
          }),
        })

        if (!res.ok) {
          console.error('Failed to invite user:', res)
          throw new Error('Failed to invite user')
        }

        const data = (await res.json()) as { invite: Invite }
        setIsInviteModalOpen(false)
        setNewlyInvitedUser({ ...data.invite, status: InviteStatus.SENT })
      } catch (error) {
        console.error('Error inviting users:', error)
        throw new Error('Error inviting users')
      }
    }

    toast.promise(handleInvite(userData), {
      loading: 'Inviting user...',
      success: 'User invited successfully',
      error: 'Failed to invite user',
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vessel Users</h2>
          <p>
            Manage users for{' '}
            {connectedAccounts.find((account) => account.id === activeConnectedAccountId)?.name || 'vessel'}
          </p>
        </div>
        <Button
          color="blue"
          className="font-medium"
          onClick={() => setIsInviteModalOpen(true)}
        >
          + Invite User
        </Button>
      </div>
      <Tabs className="mb-6">
        <TabItem
          to="/vessel-users/list"
          current={location.pathname === '/vessel-users/list'}
        >
          Active Users
        </TabItem>
        <TabItem
          to="/vessel-users/invites"
          current={location.pathname === '/vessel-users/invites'}
        >
          Invites
        </TabItem>
      </Tabs>

      {activeConnectedAccountId && (
        <InviteUserModal
          isOpen={isInviteModalOpen}
          enforceInviteToConnectedAccountId={activeConnectedAccountId}
          onClose={() => setIsInviteModalOpen(false)}
          onInvite={handleUserInvite}
        />
      )}

      <Outlet context={{ newlyInvitedUser }} />
    </>
  )
}

export default ConnectedAccountUsers
